"use client";

import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { findNameClash } from "@/lib/categories/name-clash";
import { ancestorsOf } from "@/lib/categories/tree-model";
import { CustomerSlugInput } from "./customer-slug-input";
import { customerSlugError } from "@/lib/categories/attribute-slugs";
import { deriveCategorySlug } from "@/lib/categories/slug";
import {
  completeLanguageMap,
  localizedText,
  sparseLanguageMap,
} from "@/lib/categories/localization";
import { languageName, type LanguageCode } from "@/constants/languages";
import type {
  CategoryTreeNode,
  LanguageMap,
  OptionalLanguageMap,
} from "@/services/category.service";

export type CategoryFormMode = "create" | "rename";

export interface CategoryFormValues {
  name: LanguageMap;
  description: string | null;
  slug: string;
  category_slug: OptionalLanguageMap | null;
}

interface CategoryFormDialogProps {
  open: boolean;
  mode: CategoryFormMode;
  parent: CategoryTreeNode | null;
  target: CategoryTreeNode | null;
  nodes: CategoryTreeNode[];
  languages: LanguageCode[];
  busy: boolean;
  error: string | null;
  onSubmit: (values: CategoryFormValues) => void;
  onOpenChange: (open: boolean) => void;
}

const emptyValues = (languages: readonly string[]) =>
  Object.fromEntries(languages.map((language) => [language, ""]));

export function CategoryFormDialog({
  open,
  mode,
  parent,
  target,
  nodes,
  languages,
  busy,
  error,
  onSubmit,
  onOpenChange,
}: CategoryFormDialogProps) {
  const [names, setNames] = useState<Record<string, string>>({});
  const [description, setDescription] = useState("");
  const [categorySlugs, setCategorySlugs] = useState<Record<string, string>>({});
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);

  const isCreate = mode === "create";
  const englishName = names.en ?? "";

  useEffect(() => {
    if (!open) return;
    setNames(
      mode === "rename" && target
        ? Object.fromEntries(languages.map((language) => [language, target.name[language] ?? ""]))
        : emptyValues(languages),
    );
    setDescription(mode === "rename" ? (target?.description ?? "") : "");
    setSlug("");
    setCategorySlugs(
      mode === "rename" && target?.category_slug
        ? Object.fromEntries(
            languages.map((language) => [language, target.category_slug?.[language] ?? ""]),
          )
        : emptyValues(languages),
    );
    setSlugEdited(false);
  }, [open, mode, target, languages]);

  const clash = useMemo(
    () => findNameClash(nodes, englishName, mode === "rename" ? target?.id : null),
    [nodes, englishName, mode, target],
  );

  const trimmedSlug = slug.trim();
  const allNamesPresent = languages.every((language) => names[language]?.trim());
  const slugError = languages
    .map((language) => customerSlugError(categorySlugs[language] ?? ""))
    .find(Boolean);
  const canSubmit =
    allNamesPresent
    && (!isCreate || trimmedSlug.length > 0)
    && !clash
    && !busy
    && !slugError;

  const submit = () => {
    const category_slug = sparseLanguageMap(languages, categorySlugs);
    onSubmit({
      name: completeLanguageMap(languages, names),
      description: description.trim() || null,
      slug: trimmedSlug,
      category_slug: Object.keys(category_slug).length ? category_slug : null,
    });
  };

  const title = isCreate
    ? parent
      ? `Add a subcategory under “${localizedText(parent.name)}”`
      : "Add a top-level category"
    : `Edit “${target ? localizedText(target.name) : ""}”`;

  const clashLocation = useMemo(() => {
    if (!clash) return "";
    const path = ancestorsOf(nodes, clash.id).map((category) => localizedText(category.name));
    return path.length ? ` inside ${path.join(" › ")}` : " at the top level";
  }, [clash, nodes]);

  const hint = !englishName.trim()
    ? "English is required and must be unique."
    : clash
      ? `“${localizedText(clash.name)}” already exists${clashLocation}.`
      : "Available.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {isCreate ? (
            <DialogDescription>
              Add a name for every catalogue language. The identifier remains language-neutral.
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            {languages.map((language) => (
              <div key={language} className="space-y-2">
                <Label htmlFor={`category-name-${language}`}>
                  {languageName(language)} name
                </Label>
                <Input
                  id={`category-name-${language}`}
                  autoFocus={language === "en"}
                  autoComplete="off"
                  value={names[language] ?? ""}
                  placeholder={language === "en" ? "e.g. Armchairs" : "Required"}
                  className={cn(language === "en" && clash && "border-destructive")}
                  aria-invalid={language === "en" ? !!clash : !names[language]?.trim()}
                  onChange={(event) => {
                    const value = event.target.value;
                    setNames((current) => ({ ...current, [language]: value }));
                    if (language === "en" && isCreate && !slugEdited) {
                      setSlug(deriveCategorySlug(value));
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && canSubmit) submit();
                  }}
                />
              </div>
            ))}
          </div>
          <p className={cn("text-xs", clash ? "text-destructive" : "text-muted-foreground")}>
            {hint}
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            {languages.map((language) => (
              <CustomerSlugInput
                key={language}
                id={`category-customer-slug-${language}`}
                label={`${languageName(language)} category slug`}
                value={categorySlugs[language] ?? ""}
                disabled={busy}
                onChange={(value) =>
                  setCategorySlugs((current) => ({ ...current, [language]: value }))
                }
              />
            ))}
          </div>
          {slugError ? <p className="text-xs text-destructive">{slugError}</p> : null}

          {isCreate ? (
            <div className="space-y-2">
              <Label htmlFor="category-slug">Identifier</Label>
              <Input
                id="category-slug"
                autoComplete="off"
                maxLength={255}
                value={slug}
                placeholder="e.g. armchairs"
                onChange={(event) => {
                  setSlug(event.target.value);
                  setSlugEdited(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && canSubmit) submit();
                }}
              />
              <p className="text-xs text-muted-foreground">
                A unique language-neutral identifier for imports and integrations.
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="category-description">
              Description <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="category-description"
              rows={3}
              value={description}
              disabled={busy}
              placeholder="Classification guidance for this category"
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {isCreate ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
